import * as React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { DirectionProvider } from '@radix-ui/react-direction';

import { useDirection } from '@/hooks/useDirection';
import { useSession } from '@/hooks/useSession';
import { Header } from '@/components/Header';
import { TrialGate } from '@/components/TrialGate';
import { ConfirmProvider } from '@/components/ConfirmDialog';
import { Toaster } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';
import type { SessionUser } from '@/lib/api';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AccessDenied from '@/pages/AccessDenied';
import Dashboard from '@/pages/Dashboard';
import Customers from '@/pages/Customers';
import Questionnaires from '@/pages/Questionnaires';
import QuestionnaireEdit from '@/pages/QuestionnaireEdit';
import Cycles from '@/pages/Cycles';
import Records from '@/pages/Records';
import Settings from '@/pages/Settings';
import AdminOrgs from '@/pages/AdminOrgs';
import Help from '@/pages/Help';

function AppShell({ user, children }: { user: SessionUser; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-6">
        <TrialGate isSuperAdmin={user.isSuperAdmin}>{children}</TrialGate>
      </main>
    </div>
  );
}

function RequireAuth({ children }: { children: (u: SessionUser) => React.ReactNode }) {
  const state = useSession();
  const location = useLocation();
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }
  if (!state.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <AppShell user={state.user}>{children(state.user)}</AppShell>;
}

function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const state = useSession();
  if (state.loading) return null;
  if (!state.user) return <Navigate to="/login" replace />;
  if (!state.user.isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  const dir = useDirection();
  return (
    <DirectionProvider dir={dir}>
      <ConfirmProvider>
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/denied" element={<AccessDenied />} />

        <Route path="/dashboard" element={<RequireAuth>{() => <Dashboard />}</RequireAuth>} />
        <Route path="/customers" element={<RequireAuth>{() => <Customers />}</RequireAuth>} />
        <Route
          path="/questionnaires"
          element={<RequireAuth>{() => <Questionnaires />}</RequireAuth>}
        />
        <Route
          path="/questionnaires/:id"
          element={<RequireAuth>{() => <QuestionnaireEdit />}</RequireAuth>}
        />
        <Route path="/cycles" element={<RequireAuth>{() => <Cycles />}</RequireAuth>} />
        <Route path="/records" element={<RequireAuth>{() => <Records />}</RequireAuth>} />
        <Route path="/settings" element={<RequireAuth>{() => <Settings />}</RequireAuth>} />
        <Route path="/help" element={<RequireAuth>{() => <Help />}</RequireAuth>} />

        <Route
          path="/admin"
          element={
            <RequireAuth>
              {(u) => (
                <RequireSuperAdmin>
                  <AdminOrgs user={u} />
                </RequireSuperAdmin>
              )}
            </RequireAuth>
          }
        />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </ConfirmProvider>
    </DirectionProvider>
  );
}
